import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { uploadFilesForCourseExtraction, pollProcessingResult, createProgram } from '../services/api';
import './AddProgramCourses.css';

const AddProgramCourses = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [courseList, setCourseList] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedCanvasCourses, setSelectedCanvasCourses] = useState([]);
  const [programName, setProgramName] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  
  // API and processing states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedCourses, setExtractedCourses] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState({
    programName: '',
    courses: []
  });
  const [countdown, setCountdown] = useState(30);

  const availableCanvasCourses = [
    'BSM-BADM310',
    'BSM-BADM311', 
    'BSM-BADM312',
    'BSM-BADM313',
    'BSM-BADM314'
  ];
  
  // Get program data from navigation state
  const programData = location.state?.programData || {
    name: 'New Program',
    category: 'Undergraduate',
    description: ''
  };

  // Check for existing processing job on component mount
  useEffect(() => {
    const checkExistingJob = () => {
      const storedJobId = localStorage.getItem('processing_job_id');
      const storedTimestamp = localStorage.getItem('processing_job_timestamp');
      
      if (storedJobId && storedTimestamp) {
        // Check if the job is not too old (e.g., less than 24 hours)
        const jobAge = Date.now() - parseInt(storedTimestamp);
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        if (jobAge < maxAge) {
          //console.log('Found existing processing job:', storedJobId);
          setProcessingId(storedJobId);
          setIsProcessing(true);
          setCountdown(30);
        } else {
          // Remove old job data
          localStorage.removeItem('processing_job_id');
          localStorage.removeItem('processing_job_timestamp');
        }
      }
    };
    
    checkExistingJob();
  }, []);

  const handleBack = () => {
    // Get school from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND';
    
    navigate(`/programs-assessment?school=${school}`);
  };

  const handleSubmit = async () => {
    if (selectedMethod === 'canvas') {
      // For Canvas courses, only check course selection and program name
      if (selectedCanvasCourses.length === 0) {
        alert('Please select at least one Canvas course');
        return;
      }
      if (!programName.trim()) {
        alert('Please enter a program name');
        return;
      }
    } else {
      // For file upload, check files
      if (uploadedFiles.length === 0) {
        alert('Please upload files');
        return;
      }
    }

    // Check if there's already a processing job
    const existingJobId = localStorage.getItem('processing_job_id');
    if (existingJobId && isProcessing) {
      alert('A program is already being created. Please wait for it to complete before creating another one.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (selectedMethod === 'canvas') {
        // For Canvas courses, create program directly without file processing
        const urlParams = new URLSearchParams(window.location.search);
        const school = urlParams.get('school') || 'MARYLAND';
        
        console.log('Creating program with Canvas courses:', {
          program_name: programName.trim(),
          school: school,
          course_codes: selectedCanvasCourses
        });

        // Use the API function from api.js to create program directly
        const result = await createProgram(
          programName.trim(),
          school,
          selectedCanvasCourses
        );
        
        console.log('Program created successfully:', result);
        
        // Navigate back to programs list
        navigate(`/programs-assessment?school=${school}`);
        
      } else {
        // For file upload method, use existing flow
        const formData = new FormData();
        
        // Add school parameter from URL
        const urlParams = new URLSearchParams(window.location.search);
        const school = urlParams.get('school') || 'MARYLAND';
        formData.append('school', school);
        
        // Add all uploaded files to FormData
        uploadedFiles.forEach((file, index) => {
          formData.append('files', file);
        });
        
        //console.log('Uploading files to API:', uploadedFiles.length);
        
        // Call the API function from api.js
        const result = await uploadFilesForCourseExtraction(formData);
        //console.log('API response:', result);
        
        // Assuming the API returns an ID for processing
        const processingId = result.job_id || "8b0757bd-c91a-43c4-bac2-c84997c89cfc";
        
        // Store job_id and timestamp in localStorage
        localStorage.setItem('processing_job_id', processingId);
        localStorage.setItem('processing_job_timestamp', Date.now().toString());
        
        setProcessingId(processingId);
        setIsProcessing(true);
        
        //console.log('Started processing with ID:', processingId);
      }
      
    } catch (error) {
      console.error('Error submitting:', error);
      alert('Error creating program. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redis polling effect
  useEffect(() => {
    let pollInterval;
    let countdownInterval;
    
    const pollRedis = async () => {
      if (!processingId || !isProcessing) return;
      
      try {
        //console.log(`Polling Redis for ID: ${processingId}`);
        
        // Use the API function from api.js
        const result = await pollProcessingResult(processingId);
        
        //console.log('Redis result:', result);
        
        if (result && result !== "") {
          // Parse the result - it should be a JSON array of courses
          let courses;
          if (typeof result === 'string') {
            courses = JSON.parse(result);
          } else {
            courses = result;
          }
          
          //console.log('Extracted courses:', courses);
          
          setExtractedCourses(courses);
          setConfirmationData({
            programName: programData.name,
            courses: courses
          });
          setIsProcessing(false);
          setShowConfirmation(true);
          
          // Remove job from localStorage when processing is complete
          localStorage.removeItem('processing_job_id');
          localStorage.removeItem('processing_job_timestamp');
          
          if (pollInterval) {
            clearInterval(pollInterval);
          }
          if (countdownInterval) {
            clearInterval(countdownInterval);
          }
        } else {
          //console.log('Redis returned empty or null, continuing to poll...');
          // Reset countdown for next poll
          setCountdown(30);
        }
      } catch (error) {
        console.error('Error polling Redis:', error);
      }
    };

    if (processingId && isProcessing) {
      // Start polling every 30 seconds
      pollInterval = setInterval(() => {
        pollRedis();
      }, 30000);
      
      // Start countdown timer that decrements every second
      countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            return 30; // Reset to 30 when reaching 0
          }
          return prev - 1;
        });
      }, 1000);
      
      // Poll immediately and reset countdown
      pollRedis();
      setCountdown(30);
      
      //console.log('Started Redis polling for processing ID:', processingId);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        //console.log('Stopped Redis polling');
      }
      if (countdownInterval) {
        clearInterval(countdownInterval);
        //console.log('Stopped countdown timer');
      }
    };
  }, [processingId, isProcessing, programData.name]);

  const handleConfirmProgram = async () => {
    try {
      // Get school from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const school = urlParams.get('school') || 'MARYLAND';

      console.log('Creating program with data:', {
        program_name: confirmationData.programName,
        school: school,
        course_codes: confirmationData.courses
      });

      // Use the API function from api.js
      const result = await createProgram(
        confirmationData.programName,
        school,
        confirmationData.courses
      );
      
      //console.log('Program created successfully:', result);
      
      // Navigate back to programs list
      navigate(`/programs-assessment?school=${school}`);
    } catch (error) {
      console.error('Error creating program:', error);
      alert('Error creating program. Please try again.');
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setIsProcessing(false);
    setProcessingId(null);
    setExtractedCourses([]);
    
    // Remove job from localStorage when user cancels
    localStorage.removeItem('processing_job_id');
    localStorage.removeItem('processing_job_timestamp');
  };

  const handleCourseEdit = (index, newValue) => {
    setConfirmationData(prev => ({
      ...prev,
      courses: prev.courses.map((course, i) => i === index ? newValue : course)
    }));
  };

  const handleRemoveCourse = (index) => {
    setConfirmationData(prev => ({
      ...prev,
      courses: prev.courses.filter((_, i) => i !== index)
    }));
  };

  const handleAddCourse = () => {
    setConfirmationData(prev => ({
      ...prev,
      courses: [...prev.courses, 'New Course']
    }));
  };

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
  };

  const handleCourseListChange = (e) => {
    setCourseList(e.target.value);
  };

  const handleAddManualCourse = () => {
    if (courseList.trim()) {
      const courses = courseList.split('\n').filter(course => course.trim());
      setSelectedCourses(prev => [...prev, ...courses]);
      setCourseList('');
    }
  };

  // Canvas course selection handlers
  const handleCanvasCourseToggle = (course) => {
    setSelectedCanvasCourses(prev => {
      if (prev.includes(course)) {
        return prev.filter(c => c !== course);
      } else {
        return [...prev, course];
      }
    });
  };

  const handleSelectAllCanvasCourses = () => {
    
    
    if (selectedCanvasCourses.length === availableCanvasCourses.length) {
      setSelectedCanvasCourses([]);
    } else {
      setSelectedCanvasCourses(availableCanvasCourses);
    }
  };

  // File processing function
  const processFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray;

    setUploadedFiles(prev => [...prev, ...validFiles]);
    // Files will be sent to backend via API - no need to read content here
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  };

  // File input handler
  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      processFiles(files);
    }
  };

  // Click handler for upload area
  const handleUploadAreaClick = () => {
    document.getElementById('file-input').click();
  };

  return (
    <div className="add-program-courses">
      {/* Header with Title and Back Button */}
      <div className="page-header">
        <h1>Create Program</h1>
        <button className="back-button-1" onClick={handleBack}>
          <span className="back-icon">←</span>
          Back
        </button>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Warning message for active processing */}
        {isProcessing && (
          <div className="processing-warning">
            <div className="warning-icon">⚠️</div>
            <div className="warning-text">
              <strong>Program Creation in Progress</strong>
              <p>A program is currently being created. You cannot start another program creation until this one completes.</p>
            </div>
          </div>
        )}
        
        <div className="content-header">
          <p>Choose one of the following methods to add courses to your program.</p>
        </div>

        {/* Method Selection Cards */}
        <div className="method-selection">
          <div 
            className={`method-card ${selectedMethod === 'upload' ? 'selected' : ''} ${isProcessing ? 'disabled' : ''}`}
            onClick={() => !isProcessing && handleMethodSelect('upload')}
          >
            <div className="method-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
            </div>
            <h3>Upload Course Documents</h3>
            <p>Upload course documents to create a program</p>
          </div>

          <div 
            className="method-card disabled"
          >
            <div className="method-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9"/>
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
            </div>
            <h3>Add Course Manually</h3>
            <p>Enter course codes one by one (Coming Soon)</p>
          </div>

          <div 
            className={`method-card ${selectedMethod === 'canvas' ? 'selected' : ''} ${isProcessing ? 'disabled' : ''}`}
            onClick={() => !isProcessing && handleMethodSelect('canvas')}
          >
            <div className="method-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
            </div>
            <h3>Select from Canvas Integrated Courses</h3>
            <p>Choose from available Canvas integrated courses</p>
          </div>
        </div>

        {/* Course Input Section */}
        {selectedMethod === 'upload' && (
          <div className={`course-input-section ${isProcessing ? 'disabled' : ''}`}>
            <h3>Upload Course List</h3>
            <div 
              className={`upload-area ${isDragOver ? 'drag-over' : ''} ${isProcessing ? 'disabled' : ''}`}
              onDragEnter={!isProcessing ? handleDragEnter : undefined}
              onDragLeave={!isProcessing ? handleDragLeave : undefined}
              onDragOver={!isProcessing ? handleDragOver : undefined}
              onDrop={!isProcessing ? handleDrop : undefined}
              onClick={!isProcessing ? handleUploadAreaClick : undefined}
            >
              <div className="upload-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>Drag and drop multiple files here or click to browse (PDF Only)</p>
                <input 
                  type="file" 
                  id="file-input" 
                  multiple
                  style={{ display: 'none' }} 
                  onChange={!isProcessing ? handleFileInput : undefined}
                  disabled={isProcessing}
                />
              </div>
            </div>
            
            {uploadedFiles.length > 0 && (
              <div className="uploaded-files">
                <h4>Uploaded Files:</h4>
                <div className="file-list">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="file-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{file.name}</span>
                      <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {selectedMethod === 'canvas' && (
          <div className={`course-input-section ${isProcessing ? 'disabled' : ''}`}>
            <h3>Create Program from Canvas Integrated Courses</h3>
            
            <div className="program-name-section">
              <label htmlFor="program-name-input">Program Name:</label>
              <input
                id="program-name-input"
                type="text"
                value={programName}
                onChange={(e) => setProgramName(e.target.value)}
                placeholder="Enter program name (e.g., Business Administration)"
                disabled={isProcessing}
                className="program-name-input"
              />
            </div>
            
            <div className="canvas-courses-container">
              <div className="canvas-courses-header">
                <button 
                  className="select-all-btn"
                  onClick={!isProcessing ? handleSelectAllCanvasCourses : undefined}
                  disabled={isProcessing}
                >
                  {selectedCanvasCourses.length === 6 ? 'Deselect All' : 'Select All'}
                </button>
                <span className="selected-count">
                  {selectedCanvasCourses.length} of {availableCanvasCourses.length} courses selected
                </span>
              </div>
              
              <div className="canvas-courses-grid">
                {[
                  'BSM-BADM310',
                  'BSM-BADM311', 
                  'BSM-BADM312',
                  'BSM-BADM313',
                  'BSM-BADM314',
                ].map((course) => (
                  <div 
                    key={course}
                    className={`canvas-course-item ${selectedCanvasCourses.includes(course) ? 'selected' : ''} ${isProcessing ? 'disabled' : ''}`}
                    onClick={() => !isProcessing && handleCanvasCourseToggle(course)}
                  >
                    <div className="course-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedCanvasCourses.includes(course)}
                        onChange={() => handleCanvasCourseToggle(course)}
                        disabled={isProcessing}
                      />
                    </div>
                    <div className="course-info">
                      <span className="course-code">{course}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Footer Navigation */}
      <div className="footer-navigation">
        <div></div> {/* Empty div for spacing */}
        
        {isProcessing ? (
          <div className="processing-status">
            <div className="processing-indicator">
              <div className="spinner"></div>
              <div className="processing-text">
                <span>Processing files... This may take a few minutes.</span>
                <span className="countdown-text">Next check in {countdown} seconds</span>
              </div>
            </div>
          </div>
        ) : (
          <button 
            className="save-draft-btn" 
            onClick={handleSubmit}
            disabled={isSubmitting || (
              selectedMethod === 'canvas' 
                ? (selectedCanvasCourses.length === 0 || !programName.trim())
                : uploadedFiles.length === 0
            )}
          >
            {isSubmitting ? (selectedMethod === 'canvas' ? 'Creating Program...' : 'Submitting...') : (selectedMethod === 'canvas' ? 'Create Program' : 'Submit')}
          </button>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="confirmation-modal">
          <div className="confirmation-content">
            <h2>Confirm Program Details</h2>
            
            <div className="confirmation-section">
              <label>Program Name:</label>
              <input 
                type="text" 
                value={confirmationData.programName}
                onChange={(e) => setConfirmationData(prev => ({
                  ...prev,
                  programName: e.target.value
                }))}
              />
            </div>
            
            <div className="confirmation-section">
              <label>Courses ({confirmationData.courses.length}):</label>
              <div className="editable-courses">
                {confirmationData.courses.map((course, index) => (
                  <div key={index} className="course-input-row">
                    <input
                      type="text"
                      value={course}
                      onChange={(e) => handleCourseEdit(index, e.target.value)}
                      className="course-input"
                      placeholder="Course code"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveCourse(index)}
                      className="remove-course-btn"
                      title="Remove course"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddCourse}
                  className="add-course-btn"
                >
                  + Add Course
                </button>
              </div>
            </div>
            
            <div className="confirmation-actions">
              <button className="cancel-btn" onClick={handleCancelConfirmation}>
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleConfirmProgram}>
                Create Program
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProgramCourses; 